import { Vec4, clamp, UIRenderer, _decorator } from 'cc';
import ShaderManipulator from './ShaderManipulator';

const { ccclass, property, requireComponent, executeInEditMode } = _decorator;
const tempVec4 = new Vec4();

@ccclass('ColorLevelAdjuster')
@requireComponent(UIRenderer)
@executeInEditMode
export default class ColorLevelAdjuster extends ShaderManipulator {
    @property
    private _levelConfig: Vec4 = new Vec4(255, 0, 255, 0);

    @property
    private _gamma: number = 1.0;

    @property({ min: 0.01, max: 9.99, step: 0.01, slide: true })
    public set gamma(value: number) {
        if (this._gamma === value) {
            return;
        }
        this._gamma = value;
        this.syncGammaProperty();
    }
    public get gamma() {
        return this._gamma;
    }

    @property({ min: 0, max: 255, step: 1, slide: true })
    public set inputWhite(value: number) {
        if (this._levelConfig.x === value) {
            return;
        }
        this._levelConfig.x = value;
        this.syncLevelConfigProperty();
    }
    public get inputWhite() {
        return this._levelConfig.x;
    }

    @property({ min: 0, max: 255, step: 1, slide: true })
    public set inputBlack(value: number) {
        if (this._levelConfig.y === value) {
            return;
        }
        this._levelConfig.y = value;
        this.syncLevelConfigProperty();
    }
    public get inputBlack() {
        return this._levelConfig.y;
    }

    @property({ min: 0, max: 255, step: 1, slide: true })
    public set outputWhite(value: number) {
        if (this._levelConfig.z === value) {
            return;
        }
        this._levelConfig.z = value;
        this.syncLevelConfigProperty();
    }
    public get outputWhite() {
        return this._levelConfig.z;
    }

    @property({ min: 0, max: 255, step: 1, slide: true })
    public set outputBlack(value: number) {
        if (this._levelConfig.w === value) {
            return;
        }
        this._levelConfig.w = value;
        this.syncLevelConfigProperty();
    }
    public get outputBlack() {
        return this._levelConfig.w;
    }

    public get levelConfig(): Readonly<Vec4> {
        return this._levelConfig;
    }
    public set levelConfig(value: Vec4) {
        if (this._levelConfig.equals(value)) {
            return;
        }
        // Ensure the value is within the range of 0 to 255
        value.x = clamp(value.x, 0, 255);
        value.y = clamp(value.y, 0, 255);
        value.z = clamp(value.z, 0, 255);
        value.w = clamp(value.w, 0, 255);
        this._levelConfig.set(value);
        this.syncLevelConfigProperty();
    }

    protected syncProperty(): void {
        this.syncLevelConfigProperty();
        this.syncGammaProperty();
    }

    protected syncLevelConfigProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        // Normalize the levelConfig values to the range of 0 to 1
        pass.setUniform(pass.getHandle('levelConfig'), tempVec4.set(this._levelConfig).multiplyScalar(1 / 255));
    }

    protected syncGammaProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        pass.setUniform(pass.getHandle('gamma'), clamp(this._gamma, 0.01, 9.99));
    }
}
